import { Euler, PerspectiveCamera, Vector3 } from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { BALANCE } from '../sim/balance';
import { Btn, makeCommand, type Command } from '../net/protocol';
import type { MallPhysics } from './physics';
import type { Input } from './input';
import { PITCH_CLAMP } from './input';

/**
 * THE FIRST-PERSON CONTROLLER.
 *
 * This is the thing the whole game is felt through, so it is worth being precise
 * about what it is doing and why.
 *
 * MOVEMENT IS ACCELERATED, NOT SET. Writing the input direction straight into
 * velocity gives movement that starts and stops on the exact frame the key does,
 * which reads as weightless — and it makes encumbrance invisible, because a
 * 40kg thief and an empty one both reach top speed instantly and only differ in
 * a number. Accelerating toward a target speed means a loaded thief takes real
 * time to get going and real distance to stop, which is the entire point of the
 * weight system. Ground acceleration is roughly four times air acceleration, so
 * you commit to a jump.
 *
 * GRAVITY IS INTEGRATED HERE, not by Rapier. The character is kinematic — Rapier
 * moves it exactly where it is told and never applies a force to it — so the fall
 * is ours to run. `computeColliderMovement` then tells us how much of the
 * requested translation actually fits.
 *
 * THE CAMERA IS NOT THE BODY. Yaw and pitch live on this object and are written
 * to the camera each frame together with the eye offset, the crouch lerp and the
 * head bob. Nothing outside this file ever sets the camera transform while a
 * round is running, or the two fight.
 */

const M = BALANCE.move;

/** Rapier's collide-and-slide skin. Too small and the capsule wedges in seams. */
const SKIN = 0.02;
/** How long after walking off a ledge a jump still counts. Standard platformer grace. */
const COYOTE_SEC = 0.12;
/** A jump pressed this long before landing fires on touchdown. */
const JUMP_BUFFER_SEC = 0.14;
/** Vertical travel per bob cycle, in metres, at full walk speed. */
const BOB_AMPLITUDE = 0.032;
const BOB_LATERAL = 0.018;

export interface PlayerFrame {
  /** Ground speed in metres per second, for the HUD and the noise model. */
  speed: number;
  grounded: boolean;
  crouching: boolean;
  sprinting: boolean;
  /** 0..1. Sprinting drains it; standing refills it. */
  stamina: number;
  /** Metres travelled this frame. Drives the footstep/noise emitter. */
  travelled: number;
}

export class Player {
  readonly position = new Vector3();
  readonly velocity = new Vector3();
  yaw = 0;
  pitch = 0;

  /** Multiplier from encumbrance and consumables. The session writes it each frame. */
  speedScale = 1;
  jumpScale = 1;
  /** Set false during the results screen and while a menu is open. */
  enabled = true;

  private readonly controller: RAPIER.KinematicCharacterController;
  private readonly collider: RAPIER.Collider;
  private readonly body: RAPIER.RigidBody;
  private stamina = 1;
  private grounded = false;
  private groundedFor = 0;
  private airborneFor = 0;
  private jumpBuffered = 0;
  private crouchAmount = 0;
  private bobPhase = 0;
  private bobLevel = 0;
  private readonly euler = new Euler(0, 0, 0, 'YXZ');
  private readonly desired = new Vector3();
  private readonly scratch = new Vector3();

  constructor(
    private readonly physics: MallPhysics,
    spawn: { x: number; y: number; z: number },
  ) {
    const world = physics.world;
    this.position.set(spawn.x, spawn.y + 0.1, spawn.z);

    this.body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        this.position.x,
        this.position.y + M.standHeight / 2,
        this.position.z,
      ),
    );
    this.collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(halfHeight(M.standHeight), M.radius),
      this.body,
    );
    // Every sight line starts inside this capsule, so it must not occlude; and
    // teammates slide through one another rather than blocking doorways.
    physics.registerCrowd(this.collider);

    const c = world.createCharacterController(SKIN);
    // Autostep over kerbs, thresholds and the lip of an escalator without a jump.
    // The `false` is "do not autostep onto dynamic bodies" — there are none, but
    // it also means the controller will not try to climb a moving thing later.
    c.enableAutostep(M.stepHeight, M.radius * 0.75, false);
    // Sticks the capsule to the floor over small drops, so walking down the
    // stair ramp does not fire the airborne state once per step and kill the bob.
    c.enableSnapToGround(0.5);
    c.setMaxSlopeClimbAngle((52 * Math.PI) / 180);
    c.setMinSlopeSlideAngle((44 * Math.PI) / 180);
    c.setApplyImpulsesToDynamicBodies(false);
    // Slide along walls rather than stopping dead against them. Without this,
    // brushing a doorframe at a shallow angle brings the player to a halt.
    c.setSlideEnabled(true);
    this.controller = c;
  }

  get eyeHeight(): number {
    const h = M.standHeight + (M.crouchHeight - M.standHeight) * this.crouchAmount;
    return h + M.eyeOffset;
  }

  get isCrouching(): boolean {
    return this.crouchAmount > 0.5;
  }

  teleport(to: { x: number; y: number; z: number }, yaw = this.yaw): void {
    this.position.set(to.x, to.y + 0.1, to.z);
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.pitch = 0;
    this.syncBody();
  }

  /** Unit vector the eyes are pointing along. Interaction and guards both need it. */
  forward(out = new Vector3()): Vector3 {
    this.euler.set(this.pitch, this.yaw, 0);
    return out.set(0, 0, -1).applyEuler(this.euler);
  }

  /**
   * Build this tick's command from the keyboard and mouse.
   *
   * Split out from `step` because the host has to run `step` for players whose
   * keyboards are on another continent. Everything that reads the local machine's
   * peripherals happens here and produces seven bytes; everything that moves a
   * body happens there and takes those seven bytes as its only input. That is the
   * whole shape of the netcode, and keeping the seam honest is what stops
   * "remote players move slightly differently" from ever being possible.
   */
  sampleCommand(input: Input, seq: number, out: Command = makeCommand()): Command {
    if (this.enabled && input.locked) {
      const look = input.takeLook();
      this.yaw += look.yaw;
      this.pitch = clamp(this.pitch + look.pitch, -PITCH_CLAMP, PITCH_CLAMP);
    } else {
      input.takeLook();
    }
    const s = this.enabled ? input.sample() : NEUTRAL;
    out.seq = seq;
    out.forward = Math.sign(s.forward);
    out.strafe = Math.sign(s.strafe);
    out.buttons =
      (s.jump ? Btn.Jump : 0) |
      (s.sprint ? Btn.Sprint : 0) |
      (s.crouch ? Btn.Crouch : 0) |
      (input.isHeld('interact') && input.locked ? Btn.Interact : 0);
    out.yaw = this.yaw;
    out.pitch = this.pitch;
    return out;
  }

  update(dt: number, input: Input, camera: PerspectiveCamera): PlayerFrame {
    const frame = this.step(dt, this.sampleCommand(input, 0, this.localCmd));
    this.writeCamera(camera);
    return frame;
  }

  private readonly localCmd: Command = makeCommand();

  /**
   * One fixed step of movement, from one command and nothing else.
   *
   * No input object, no camera, no clock. A pure-ish function of (this body's
   * state, the command, the static world) — which is precisely the property that
   * lets the host re-run it for a remote player and get the answer that player's
   * own machine got, and lets a client re-run its own unacknowledged commands
   * when a correction arrives.
   */
  step(dt: number, cmd: Command): PlayerFrame {
    this.yaw = cmd.yaw;
    this.pitch = clamp(cmd.pitch, -PITCH_CLAMP, PITCH_CLAMP);
    const s = this.enabled
      ? {
          forward: cmd.forward,
          strafe: cmd.strafe,
          jump: (cmd.buttons & Btn.Jump) !== 0,
          sprint: (cmd.buttons & Btn.Sprint) !== 0,
          crouch: (cmd.buttons & Btn.Crouch) !== 0,
        }
      : NEUTRAL;

    // --- Stance ------------------------------------------------------------
    // Uncrouching is refused under a low ceiling. Checked against the STANDING
    // capsule rather than a ray, because a ray up the spine misses a shelf the
    // shoulders would hit.
    const wantCrouch = s.crouch || (this.crouchAmount > 0 && !this.hasHeadroom());
    const crouchRate = dt * 9;
    this.crouchAmount = approach(this.crouchAmount, wantCrouch ? 1 : 0, crouchRate);
    this.setCapsuleHeight(M.standHeight + (M.crouchHeight - M.standHeight) * this.crouchAmount);

    // --- Target velocity ---------------------------------------------------
    const canSprint = s.sprint && !wantCrouch && this.stamina > 0.02 && s.forward > 0;
    const base = wantCrouch ? M.crouchSpeed : canSprint ? M.runSpeed : M.walkSpeed;
    const target = base * this.speedScale;

    // Normalise the input square to a circle so diagonal is not 41% faster.
    let fx = s.strafe;
    let fz = s.forward;
    const mag = Math.hypot(fx, fz);
    if (mag > 1) {
      fx /= mag;
      fz /= mag;
    }
    const cos = Math.cos(this.yaw);
    const sin = Math.sin(this.yaw);
    // Yaw 0 looks toward -Z, so forward is (-sin, -cos) and right is (cos, -sin).
    this.desired.set(fx * cos - fz * sin, 0, -fx * sin - fz * cos).multiplyScalar(target);

    const accel = (this.grounded ? M.accelGround : M.accelAir) * dt;
    this.velocity.x = approach(this.velocity.x, this.desired.x, Math.abs(this.desired.x - this.velocity.x) * accel + accel * target * 0.35);
    this.velocity.z = approach(this.velocity.z, this.desired.z, Math.abs(this.desired.z - this.velocity.z) * accel + accel * target * 0.35);

    // --- Jump --------------------------------------------------------------
    this.jumpBuffered = s.jump ? JUMP_BUFFER_SEC : Math.max(0, this.jumpBuffered - dt);
    const coyote = this.grounded || this.airborneFor < COYOTE_SEC;
    if (this.jumpBuffered > 0 && coyote && this.velocity.y <= 0.1) {
      this.velocity.y = M.jumpSpeed * this.jumpScale * (wantCrouch ? 0.7 : 1);
      this.jumpBuffered = 0;
      this.grounded = false;
      this.airborneFor = COYOTE_SEC;
    } else {
      this.velocity.y += M.gravity * dt;
      // Terminal velocity, so a fall through a hole cannot tunnel the capsule
      // past a floor in one step.
      if (this.velocity.y < -32) this.velocity.y = -32;
    }

    // --- Collide and slide -------------------------------------------------
    this.scratch.copy(this.velocity).multiplyScalar(dt);
    this.controller.computeColliderMovement(
      this.collider,
      this.scratch,
      undefined,
      undefined,
      this.physics.notCrowd,
    );
    const moved = this.controller.computedMovement();
    const before = this.position.y;
    this.position.x += moved.x;
    this.position.y += moved.y;
    this.position.z += moved.z;

    const wasGrounded = this.grounded;
    this.grounded = this.controller.computedGrounded();
    if (this.grounded) {
      this.groundedFor += dt;
      this.airborneFor = 0;
      if (this.velocity.y < 0) this.velocity.y = 0;
    } else {
      this.airborneFor += dt;
      this.groundedFor = 0;
      // Blocked from above: stop rising rather than hovering under the ceiling.
      if (this.velocity.y > 0 && this.position.y - before < moved.y - 1e-4) this.velocity.y = 0;
    }
    void wasGrounded;
    this.syncBody();

    // --- Stamina -----------------------------------------------------------
    if (canSprint && this.grounded && Math.hypot(this.velocity.x, this.velocity.z) > 0.5) {
      this.stamina = Math.max(0, this.stamina - M.staminaDrain * dt);
    } else {
      const rate = mag > 0.1 ? M.staminaRegen * 0.5 : M.staminaRegen;
      this.stamina = Math.min(1, this.stamina + rate * dt);
    }

    // --- Head bob ----------------------------------------------------------
    // Advanced here rather than in `writeCamera` because it is integrated state:
    // it has to move once per fixed step, not once per rendered frame, or the
    // gait speeds up on a 144Hz monitor.
    const planar = Math.hypot(this.velocity.x, this.velocity.z);
    // The bob follows actual speed, not input, so it stops when you walk into a
    // wall — which is a surprisingly strong cue that you have.
    const bobTarget = this.grounded ? Math.min(1, planar / M.runSpeed) : 0;
    this.bobLevel = approach(this.bobLevel, bobTarget, dt * 6);
    this.bobPhase += planar * dt * 3.1;
    this.bobY = Math.sin(this.bobPhase * 2) * BOB_AMPLITUDE * this.bobLevel;
    this.bobX = Math.sin(this.bobPhase) * BOB_LATERAL * this.bobLevel;
    this.lean = fx;

    return {
      speed: planar,
      grounded: this.grounded,
      crouching: this.isCrouching,
      sprinting: canSprint && planar > 0.5,
      stamina: this.stamina,
      travelled: Math.hypot(moved.x, moved.z),
    };
  }

  private bobY = 0;
  private bobX = 0;
  private lean = 0;

  /** Puts the eyes where the body says they are. Local player only. */
  writeCamera(camera: PerspectiveCamera): void {
    camera.position.set(
      this.position.x + Math.cos(this.yaw) * this.bobX,
      this.position.y + this.eyeHeight + this.bobY,
      this.position.z - Math.sin(this.yaw) * this.bobX,
    );
    this.euler.set(this.pitch, this.yaw, 0);
    camera.quaternion.setFromEuler(this.euler);
    // A touch of roll into the strafe. Two degrees — enough to feel, not enough
    // to notice.
    camera.rotateZ(-this.lean * 0.035 * this.bobLevel);
  }

  private syncBody(): void {
    this.body.setNextKinematicTranslation({
      x: this.position.x,
      y: this.position.y + this.currentHeight / 2,
      z: this.position.z,
    });
    // Kinematic bodies only move on a world step, and the controller queries the
    // broad phase — so without stepping, the capsule collides against where it
    // used to be and walks through thin walls at speed.
    this.body.setTranslation(
      { x: this.position.x, y: this.position.y + this.currentHeight / 2, z: this.position.z },
      true,
    );
  }

  private currentHeight: number = M.standHeight;

  private setCapsuleHeight(h: number): void {
    if (Math.abs(h - this.currentHeight) < 1e-4) return;
    this.currentHeight = h;
    this.collider.setHalfHeight(halfHeight(h));
  }

  /**
   * Is there room to stand up? Shape-casts the standing capsule straight up from
   * the crouched position by the height difference.
   */
  private hasHeadroom(): boolean {
    const rise = M.standHeight - this.currentHeight;
    if (rise <= 1e-3) return true;
    const shape = new RAPIER.Capsule(halfHeight(this.currentHeight), M.radius * 0.95);
    const at = {
      x: this.position.x,
      y: this.position.y + this.currentHeight / 2,
      z: this.position.z,
    };
    const hit = this.physics.world.castShape(
      at,
      IDENTITY,
      { x: 0, y: 1, z: 0 },
      shape,
      0,
      rise + SKIN,
      true,
      undefined,
      undefined,
      this.collider,
      undefined,
      this.physics.notCrowd,
    );
    return !hit;
  }

  /**
   * Give the capsule back.
   *
   * Only remote players are ever disposed — the local one lives as long as the
   * page — but a lobby where four people join and leave over an evening leaks a
   * Rapier body each time without this, and a leaked kinematic body is not inert:
   * it is still in the broad phase, still stopping guards, still standing in the
   * atrium where somebody used to be.
   */
  dispose(): void {
    this.physics.forgetCollider(this.collider);
    this.physics.world.removeCollider(this.collider, false);
    this.physics.world.removeRigidBody(this.body);
  }
}

const IDENTITY = { x: 0, y: 0, z: 0, w: 1 };
const NEUTRAL = {
  forward: 0,
  strafe: 0,
  jump: false,
  sprint: false,
  crouch: false,
  yawDelta: 0,
  pitchDelta: 0,
};

/** Rapier capsules are half-height BETWEEN the cap centres, not overall. */
function halfHeight(total: number): number {
  return Math.max(0.05, total / 2 - M.radius);
}

function approach(v: number, target: number, maxStep: number): number {
  const d = target - v;
  if (Math.abs(d) <= maxStep) return target;
  return v + Math.sign(d) * maxStep;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
